import { ResourceType } from '@setvi/shared/enums';
import { ROUTES } from 'enumsV2';
import { sanitizeUrl } from 'Utils/sanitizeUrl';

import type { PreviewAsset } from '../types';

/** Search params of the preview page; it refetches the asset by these. */
export enum ProductAssetPreviewParam {
  PRODUCT_ID = 'productId',
  DOCUMENT_ID = 'documentId'
}

interface ProductAssetPreviewLocation {
  productId: string;
  documentId: string;
}

export const buildProductAssetPreviewUrl = ({
  productId,
  documentId
}: ProductAssetPreviewLocation) => {
  const params = new URLSearchParams({
    [ProductAssetPreviewParam.PRODUCT_ID]: productId,
    [ProductAssetPreviewParam.DOCUMENT_ID]: documentId
  });
  return `${ROUTES.PRODUCT_ASSET_PREVIEW}?${params.toString()}`;
};

/** Opens the public file itself in a new tab (the browser renders or downloads it). */
export const openProductAssetFile = (url: string) => {
  // A document without a url has nothing to open; `sanitizeUrl` would throw.
  if (!url) return;
  window.open(sanitizeUrl(url), '_blank', 'noopener,noreferrer');
};

/** A web page is a link, not a file: it opens as itself, never in the preview. */
export const isWebPageAsset = (asset: Pick<PreviewAsset, 'resourceTypeId'>) =>
  Number(asset.resourceTypeId) === ResourceType.WebPage;

interface OpenProductAssetProps {
  productId: string;
  asset: PreviewAsset;
  /**
   * Skip the in-app preview page and open the file itself, e.g. from the
   * Outlook add-in, whose new tab lands in the system browser without the
   * add-in session. The file url is public, so it needs no login.
   */
  fileOnly?: boolean;
}

/**
 * Opens a product asset in a new browser tab: every file goes to the in-app
 * preview page (which renders what it can and says so for what it cannot,
 * never a download on click); only a web page opens as itself.
 *
 * Must be called synchronously from the click handler — `window.open` after
 * an await is silently dropped by popup blockers.
 */
export const openProductAsset = ({
  productId,
  asset,
  fileOnly = false
}: OpenProductAssetProps) => {
  if (!asset.url) return;

  if (fileOnly || isWebPageAsset(asset)) {
    openProductAssetFile(asset.url);
    return;
  }

  window.open(
    buildProductAssetPreviewUrl({ productId, documentId: asset.id }),
    '_blank'
  );
};
